import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Calendar,
  MessageCircle,
  DollarSign,
  Star,
  TrendingUp,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ZoomIn,
  ZoomOut,
  X,
  AlertCircle
} from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS } from '@constants/admin.constants';
import { useAdmin } from '@hooks/useAdmin';
import apiService from '@services/api/api.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import StatsCard from '@components/admin/StatsCard';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const MentorDetails = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { approveDocument, rejectDocument } = useAdmin();
  
  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_MENTORS)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchMentorDetails();
  }, [mentorId]);

  const fetchMentorDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/admin/mentors/${mentorId}/details`);
      if (response.success) {
        setMentor(response?.data?.data);
      }
    } catch (error) {
      toast.error('Failed to load mentor details');
      navigate('/admin/mentors');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDocument = async (documentId) => {
    setActionLoading(true);
    try {
      const response = await approveDocument(documentId);
      if (response.success) {
        toast.success('Document approved successfully');
        fetchMentorDetails();
      }
    } catch (error) {
      toast.error('Failed to approve document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectDocument = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      const response = await rejectDocument(selectedDoc.id, rejectionReason);
      if (response.success) {
        toast.success('Document rejected');
        setShowRejectModal(false);
        setRejectionReason('');
        setSelectedDoc(null);
        fetchMentorDetails();
      }
    } catch (error) {
      toast.error('Failed to reject document');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (doc) => {
    setSelectedDoc(doc);
    setShowRejectModal(true);
  };

  const openDocViewer = (doc) => {
    setViewingDoc(doc);
    setShowDocViewer(true);
    setZoomLevel(100);
  };

  const closeDocViewer = () => {
    setShowDocViewer(false);
    setViewingDoc(null);
    setZoomLevel(100);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  if (loading || !mentor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_MENTORS}>
      <div className="min-h-full bg-gray-50 p-6">
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/mentors')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Mentors
          </Button>
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            Mentor Details
          </h1>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatsCard
            title="Questions Answered"
            value={mentor.stats?.questionsAnswered || 0}
            icon={MessageCircle}
            color="primary"
          />
          <StatsCard
            title="Pending Answer Questions"
            value={mentor.stats?.pendingQuestions || 0}
            icon={AlertCircle}
            color="red"
          />
          <StatsCard
            title="Total Earnings"
            value={`₹${mentor.stats?.totalEarnings || 0}`}
            icon={DollarSign}
            color="green"
          />
          <StatsCard
            title="Average Rating"
            value={mentor.stats?.averageRating?.toFixed(1) || '0.0'}
            icon={Star}
            color="yellow"
          />
          <StatsCard
            title="Response Rate"
            value={`${mentor.stats?.responseRate || 0}%`}
            icon={TrendingUp}
            color="blue"
          />
        </div>

        {/* Mentor Info */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {mentor.profilePicture ? (
                <img
                  src={`${import.meta.env.VITE_API_BASE_URL.replace('/api', '')}${mentor.profilePicture}`}
                  alt={`${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.email}
                  className="w-20 h-20 rounded-full object-cover shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  {mentor.firstName?.[0]?.toUpperCase() || mentor.lastName?.[0]?.toUpperCase() || mentor.email?.[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-primary-900">
                  {`${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.email}
                </h2>
              </div>
            </div>
            <div>
              {mentor.adminApproved === 'approved' ? (
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Approved
                </span>
              ) : mentor.adminApproved === 'rejected' ? (
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-700 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Rejected
                </span>
              ) : (
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pending
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary-500" />
              <div>
                <p className="text-xs text-primary-500">Email</p>
                <p className="text-sm font-medium text-primary-900">{mentor.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-primary-500" />
              <div>
                <p className="text-xs text-primary-500">Phone</p>
                <p className="text-sm font-medium text-primary-900">{mentor.phone || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary-500" />
              <div>
                <p className="text-xs text-primary-500">Joined</p>
                <p className="text-sm font-medium text-primary-900">
                  {new Date(mentor.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Recent Questions */}
        <Card className="p-6 mb-6">
          <h3 className="text-xl font-bold text-primary-900 mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Recent Questions Answered
          </h3>
          {mentor.recentQuestions && mentor.recentQuestions.length > 0 ? (
            <div className="space-y-3">
              {mentor.recentQuestions.map((question) => (
                <div key={question.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-medium text-primary-900 flex-1">
                      {question.questionText}
                    </p>
                    <span className="text-xs text-primary-500 ml-4">
                      {new Date(question.answeredAt).toLocaleDateString()}
                    </span>
                  </div>
                  {question.answerText && (
                    <p className="text-sm text-primary-600 mt-2">
                      Answer: {question.answerText}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-primary-500">
                      Student: {question.studentEmail}
                    </span>
                    {question.rating && (
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs font-semibold text-primary-900">
                          {question.rating}/5
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-primary-500 py-8">No questions answered yet</p>
          )}
        </Card>

        {/* Documents */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-primary-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Verification Documents
          </h3>
          {mentor.documents && mentor.documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {mentor.documents.map((doc) => (
                <div key={doc.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-semibold text-primary-900 mb-2">
                    {doc.documentType.replace('_', ' ').toUpperCase()}
                  </p>
                  <div className="mb-3">
                    {doc.status === 'approved' ? (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-semibold">
                        Approved
                      </span>
                    ) : doc.status === 'rejected' ? (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">
                        Rejected
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-semibold">
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDocViewer(doc)}
                    >
                      View
                    </Button>
                    {doc.status === 'pending' && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleApproveDocument(doc.id)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openRejectModal(doc)}
                          disabled={actionLoading}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-primary-500 py-8">No documents uploaded</p>
          )}
        </Card>
      </div>

      {/* Document Viewer Modal */}
      {showDocViewer && viewingDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="relative w-full h-full flex flex-col">
            {/* Header with controls */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-primary-900">
                  {viewingDoc.documentType.replace('_', ' ').toUpperCase()}
                </h3>
                <p className="text-sm text-primary-600">
                  Status: {viewingDoc.status}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 50}
                    className="p-1"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </Button>
                  <span className="text-sm font-semibold text-primary-900 min-w-[60px] text-center">
                    {zoomLevel}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 200}
                    className="p-1"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </Button>
                </div>
                {/* Close Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeDocViewer}
                  className="p-2"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
            </div>

            {/* Document Display Area */}
            <div className="flex-1 overflow-auto bg-gray-900 p-8">
              <div className="flex justify-center">
                {viewingDoc.mimeType === 'application/pdf' ? (
                  <iframe
                    src={viewingDoc.filePath}
                    title={viewingDoc.documentType}
                    className="w-full h-full shadow-2xl"
                    style={{
                      minHeight: '80vh',
                      border: 'none'
                    }}
                  />
                ) : (
                  <img
                    src={viewingDoc.filePath}
                    alt={viewingDoc.documentType}
                    style={{ 
                      width: `${zoomLevel}%`,
                      maxWidth: 'none',
                      height: 'auto'
                    }}
                    className="shadow-2xl"
                  />
                )}
              </div>
            </div>

            {/* Action Buttons for Pending Documents */}
            {viewingDoc.status === 'pending' && (
              <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    handleApproveDocument(viewingDoc.id);
                    closeDocViewer();
                  }}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Document
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    closeDocViewer();
                    openRejectModal(viewingDoc);
                  }}
                  disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Document
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-primary-900 mb-4">
              Reject Document
            </h3>
            <p className="text-sm text-primary-600 mb-4">
              Please provide a reason for rejecting this document:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 mb-4"
              rows="4"
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedDoc(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRejectDocument}
                disabled={actionLoading || !rejectionReason.trim()}
                loading={actionLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                Reject Document
              </Button>
            </div>
          </div>
        </div>
      )}
    </PermissionGuard>
  );
};

export default MentorDetails;
